import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Trash2, Archive, RotateCcw, CheckSquare, Square, AlertTriangle,
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { opts } from "./constants";

export function PapeleraModal({ open, onClose, onChanged }) {
  const { user } = useAuth();
  const role = user?.role || "";
  const canBulkDelete = role === "Superadministrador" || role === "Administrador";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [emptying, setEmptying] = useState(false);
  // confirm: { type: "single"|"bulk"|"empty", ids?: string[], name?: string }
  const [confirm, setConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/papelera/list`, opts);
      setItems(res.data || []);
      setSelected(new Set());
    } catch { toast.error("Error al cargar papelera"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  const handleRestore = async (b) => {
    try {
      await axios.post(`${API}/budgets/${b._id}/restore`, {}, opts);
      toast.success("Presupuesto restaurado");
      setSelected((prev) => { const n = new Set(prev); n.delete(b._id); return n; });
      fetchItems();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al restaurar"); }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visibleIds = useMemo(() => items.map((b) => b._id), [items]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };

  const doBulkDelete = async (ids) => {
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/budgets/papelera/bulk-delete`, { ids }, opts);
      toast.success(`${data.deleted || 0} presupuesto${(data.deleted || 0) !== 1 ? "s" : ""} eliminado${(data.deleted || 0) !== 1 ? "s" : ""} permanentemente`);
      fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setBulkDeleting(false);
      setConfirm(null);
    }
  };

  const doEmpty = async () => {
    setEmptying(true);
    try {
      const { data } = await axios.post(`${API}/budgets/papelera/empty`, {}, opts);
      toast.success(`Papelera vaciada (${data.deleted || 0} presupuesto${(data.deleted || 0) !== 1 ? "s" : ""})`);
      fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vaciar papelera");
    } finally {
      setEmptying(false);
      setConfirm(null);
    }
  };

  const daysLeft = (deletedAt) => {
    if (!deletedAt) return 30;
    const d = new Date(deletedAt);
    const passed = Math.floor((Date.now() - d.getTime()) / 86400000);
    return Math.max(0, 30 - passed);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Archive size={18} /> Papelera de Presupuestos
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Retención automática: 30 días desde eliminación</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="papelera-close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Toolbar: select-all + acciones masivas */}
        {!loading && items.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-3 flex-wrap">
            <button
              onClick={toggleAll}
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#1A4B7A] hover:text-[#2E6DA4]"
              data-testid="papelera-select-all-btn"
              aria-label={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
            >
              {allSelected ? (
                <CheckSquare size={16} />
              ) : someSelected ? (
                <CheckSquare size={16} className="opacity-60" />
              ) : (
                <Square size={16} />
              )}
              <span>{allSelected ? "Deseleccionar todos" : "Seleccionar todos"}</span>
              {selected.size > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#1A4B7A] text-white text-[10px]" data-testid="papelera-selected-count">
                  {selected.size}
                </span>
              )}
            </button>

            <div className="ml-auto flex items-center gap-2">
              {selected.size > 0 && canBulkDelete && (
                <button
                  onClick={() => setConfirm({ type: "bulk", ids: Array.from(selected) })}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
                  data-testid="papelera-bulk-delete-btn"
                >
                  {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  Eliminar seleccionados ({selected.size})
                </button>
              )}
              {canBulkDelete && (
                <button
                  onClick={() => setConfirm({ type: "empty" })}
                  disabled={emptying || items.length === 0}
                  className="flex items-center gap-1.5 border-2 border-red-300 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
                  data-testid="papelera-empty-btn"
                  title="Eliminar permanentemente TODOS los presupuestos en la papelera"
                >
                  {emptying ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                  Vaciar Papelera
                </button>
              )}
            </div>
          </div>
        )}

        <div className="p-6 flex-1 overflow-y-auto" data-testid="papelera-list">
          {loading ? (
            <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Archive size={36} className="mx-auto mb-2" />
              <p className="text-sm font-medium">Papelera vacía</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(b => {
                const dl = daysLeft(b.deleted_at);
                const isSel = selected.has(b._id);
                return (
                  <div
                    key={b._id}
                    className={`border rounded-xl p-4 flex items-center gap-3 transition-colors ${
                      isSel ? "bg-blue-50/60 border-blue-200" : "bg-gray-50 border-gray-200"
                    }`}
                    data-testid={`papelera-item-${b._id}`}
                  >
                    <button
                      onClick={() => toggleOne(b._id)}
                      className="flex-shrink-0 text-[#1A4B7A] hover:text-[#2E6DA4]"
                      data-testid={`papelera-select-${b._id}`}
                      aria-label="Seleccionar"
                    >
                      {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm break-words">{b.nombre}</p>
                      <p className="text-xs text-gray-500 mt-0.5 break-words">{b.number} · {b.cliente || "—"} · Eliminado por {b.deleted_by_name || "—"}</p>
                      <p className="text-xs mt-0.5">
                        <span className={`font-semibold ${dl <= 5 ? "text-red-600" : "text-amber-600"}`}>{dl} día{dl !== 1 ? "s" : ""} restantes</span>
                      </p>
                    </div>
                    <button onClick={() => handleRestore(b)} className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center gap-1 transition-colors" data-testid={`restore-btn-${b._id}`}>
                      <RotateCcw size={12} /> Restaurar
                    </button>
                    <button
                      onClick={() => setConfirm({ type: "single", ids: [b._id], name: b.nombre })}
                      className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      data-testid={`permanent-delete-${b._id}`}
                      aria-label="Eliminar permanentemente"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Confirmación fuerte */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="papelera-confirm-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !bulkDeleting && !emptying && setConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-gray-900">
                  {confirm.type === "empty"
                    ? "¿Vaciar la papelera completa?"
                    : confirm.type === "bulk"
                      ? `¿Eliminar ${confirm.ids.length} presupuesto${confirm.ids.length !== 1 ? "s" : ""}?`
                      : "¿Eliminar permanentemente?"}
                </h4>
                <p className="text-sm text-gray-600 mt-1.5">
                  {confirm.type === "empty"
                    ? `Se borrarán de forma definitiva los ${items.length} presupuesto${items.length !== 1 ? "s" : ""} de la papelera.`
                    : confirm.type === "single"
                      ? <>El presupuesto <span className="font-semibold">"{confirm.name}"</span> se borrará para siempre.</>
                      : `Se borrarán de forma definitiva ${confirm.ids.length} presupuesto${confirm.ids.length !== 1 ? "s" : ""}.`}
                </p>
                <p className="text-xs text-red-600 mt-2 font-bold">Esta acción NO se puede deshacer.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirm(null)}
                disabled={bulkDeleting || emptying}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="papelera-confirm-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (confirm.type === "empty") doEmpty();
                  else doBulkDelete(confirm.ids);
                }}
                disabled={bulkDeleting || emptying}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="papelera-confirm-delete-btn"
              >
                {(bulkDeleting || emptying) ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                {confirm.type === "empty" ? "Sí, vaciar papelera" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  , document.body);
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
